/**
 * NotableThreadsSection
 *
 * 주목할만한 스레드 섹션
 * - 카테고리별 Top 3 (조회수/댓글/리포스트)
 * - Year of the Threads 선정 스레드 제외
 * - MagazineSectionContainer + LineGrid + EditorPickCard 스타일
 */

import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import MagazineSectionContainer from '../magazine/MagazineSectionContainer';
import LineGrid from '../magazine/LineGrid';
import EditorPickCard from '../magazine/EditorPickCard';
import { useIsMobile } from '../../hooks/useIsMobile';
import YearEndSectionTitle from './YearEndSectionTitle';

// Notable Threads 영상 URL
const NOTABLE_THREADS_VIDEO = 'https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/yearEnd%2F2025%2Fyear_notable_threads.mp4?alt=media';

const categories = [
  { id: 'byViews', labelKey: 'yearEnd.notableThreads.topViews', metric: 'views' },
  { id: 'byReplies', labelKey: 'yearEnd.notableThreads.topReplies', metric: 'replies' },
  { id: 'byReposts', labelKey: 'yearEnd.notableThreads.topReposts', metric: 'reposts' },
];

export const NotableThreadsSection = ({ notableThreads, ownerProfile }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useIsMobile('xl');

  if (!notableThreads) {
    return null;
  }

  // 스레드 목록 렌더링 함수
  const renderThreadList = (threads, labelKey) => (
    <Stack sx={{ height: '100%' }}>
      {/* 카테고리 헤더 */}
      <Box
        sx={{
          py: isMobile ? 1.5 : 2,
          px: isMobile ? 2 : 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="overline"
          sx={{
            fontWeight: 900,
            letterSpacing: '0.1em',
            color: 'primary.main',
          }}
        >
          {t(labelKey)}
        </Typography>
      </Box>

      {/* 스레드 목록 - EditorPickCard */}
      {threads && threads.length > 0 ? (
        threads.slice(0, 3).map((thread, idx) => (
          <Box
            key={thread.id}
            sx={{
              borderBottom: idx < 2 ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            <EditorPickCard
              thread={thread}
              isMobile={isMobile}
              userID={ownerProfile?.id}
              username={ownerProfile?.username}
              profileImage={ownerProfile?.threads_profile_picture_url}
              showUser={false}
            />
          </Box>
        ))
      ) : (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('yearEnd.notableThreads.noData')}
          </Typography>
        </Box>
      )}
    </Stack>
  );

  return (
    <MagazineSectionContainer isMobile={isMobile}>
      <YearEndSectionTitle title={t('yearEnd.notableThreads.title')} />

      <LineGrid
        gap={0}
        borderColor="primary.main"
        gridColumns={isMobile ? 1 : 4}
        breakpoint="xl"
      >
        {/* 첫 번째 col: 타이틀 + 영상 (위아래 양쪽 정렬) */}
        <Grid size={{ xs: 12, xl: 3 }}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            {/* 타이틀 - CoverStoryCard 스타일 */}
            <Box sx={{ px: isMobile ? 2 : 3, py: isMobile ? 3 : 5 }}>
              <Typography
                variant={isMobile ? 'body1' : 'h5'}
                sx={{
                  fontFamily: '"RIDIBatang", "Noto Serif KR", serif',
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  lineHeight: isMobile ? 1.4 : 1.5,
                  wordBreak: 'keep-all',
                }}
              >
                {t('yearEnd.notableThreads.description', {
                  defaultValue: '가장 주목받은 글, 공감받은 글, 유용했던 글을 모았습니다.'
                })}
              </Typography>
            </Box>
            {/* 영상 (1:1 비율) */}
            <Box
              sx={{
                width: '100%',
                aspectRatio: '1/1',
                overflow: 'hidden',
                bgcolor: 'grey.100',
              }}
            >
              <video
                src={NOTABLE_THREADS_VIDEO}
                autoPlay
                loop
                muted
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          </Box>
        </Grid>

        {/* 나머지 col: 카테고리별 스레드 */}
        {categories.map((category) => (
          <Grid key={category.id} size={{ xs: 12, xl: 3 }}>
            <Box sx={{ height: '100%' }}>
              {renderThreadList(
                notableThreads[category.id],
                category.labelKey
              )}
            </Box>
          </Grid>
        ))}
      </LineGrid>
    </MagazineSectionContainer>
  );
};

export default NotableThreadsSection;
